import React from 'react';
import { css } from '@emotion/react';
import NvTooltip from 'shared/components/nv-tooltip';
import { quarterSpacing } from 'styles/global_defaults/scaffolding';

const style = css`
  flex-direction: column;
  justify-content: space-between;
  padding-right: ${quarterSpacing}px;
  min-width: 0;
`;

const valueStyle = (contentMaxWidth?: number) => css`
  width: 100%;
  max-width: ${contentMaxWidth ? `${contentMaxWidth}px` : '100%'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

type InfoSectionProps = {
  title: string;
  content: string | React.ReactNode;
  showTooltip?: boolean;
  contentMaxWidth?: number;
  showDashIfEmpty?: boolean;
};

const InfoSection = ({
  title,
  content,
  showTooltip = false,
  contentMaxWidth,
  showDashIfEmpty = false,
}: InfoSectionProps) => (
  <InfoSectionContent
    title={title}
    content={content}
    showTooltip={showTooltip}
    contentMaxWidth={contentMaxWidth}
    showDashIfEmpty={showDashIfEmpty}
  />
);

const InfoSectionContent = ({
  title,
  content,
  showTooltip,
  contentMaxWidth,
  showDashIfEmpty = false,
}: InfoSectionProps) => {
  const hasContent = content != null
    && (typeof content !== 'string' || content.trim() !== '');
  const displayContent = showDashIfEmpty && !hasContent ? '-' : content;

  const contentRef = React.useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = React.useState(false);

  React.useLayoutEffect(() => {
    const updateOverflowState = () => {
      if (!contentRef.current) return;

      setIsOverflowing(contentRef.current.scrollWidth > contentRef.current.clientWidth);
    };

    updateOverflowState();
    window.addEventListener('resize', updateOverflowState);

    return () => {
      window.removeEventListener('resize', updateOverflowState);
    };
  }, [displayContent, contentMaxWidth]);

  return (
    <div css={style} className='text-small d-flex'>
      <div className='title text-gray-2 mb-1'>
        {title}
      </div>
      <NvTooltip
        text={typeof displayContent === 'string' ? displayContent : ''}
        preventOverflow={false}
        enabled={showTooltip && isOverflowing}
      >
        <div
          css={valueStyle(contentMaxWidth)}
          ref={contentRef}
          className='value semi-bold'
        >
          {displayContent}
        </div>
      </NvTooltip>
    </div>
  );
};

export default InfoSection;
